/**
 * Interactive Google map for the acqs property detail card.
 *
 * The map lives in a hidden tab on initial render, so initialization is deferred
 * until the map panel is visible. Address-only properties are geocoded client-side
 * as a fallback; no coordinates are persisted from this display-only map.
 */
(function () {

    var DEFAULT_ZOOM = 15;
    var COMPS_DEFAULT_ZOOM = 14;
    var COMPS_EMPTY_MESSAGE = 'No mapped comparable sales are available.';
    var SUBJECT_EMPTY_MESSAGE = 'The property location is unavailable.';
    var googleMapsPromise = null;

    function hasGoogleMaps() {
        return !!(window.google && window.google.maps && window.google.maps.Map);
    }

    // Resolve once the Maps API object is ready, by POLLING — not by depending solely on
    // a load &callback. A concurrent second Maps bootstrap on the page (the shared address
    // autocomplete loads maps/api/js?...&libraries=places) trips Google's "load once" guard
    // and can stop our callback from ever firing, hanging the map on "Loading map...".
    // Polling window.google.maps is robust: whichever bootstrap wins, we init once it's up.
    function waitForGoogleMaps() {
        return new Promise(function (resolve, reject) {
            if (hasGoogleMaps()) { resolve(); return; }
            var waited = 0;
            var timer = setInterval(function () {
                if (hasGoogleMaps()) {
                    clearInterval(timer);
                    resolve();
                } else if ((waited += 100) >= 15000) {
                    clearInterval(timer);
                    reject(new Error('Google Maps failed to load.'));
                }
            }, 100);
        });
    }

    function mapsScriptPresent() {
        return !!document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]');
    }

    function loadGoogleMaps(container) {
        if (hasGoogleMaps()) return Promise.resolve();
        if (googleMapsPromise) return googleMapsPromise;

        // A Maps bootstrap is already on the page (e.g. the shared address-autocomplete
        // loader, which requests libraries=places). Reuse it — NEVER inject a second
        // maps/api/js script (double-loading the API is what warns + hangs the map).
        if (!mapsScriptPresent()) {
            var key = container.getAttribute('data-google-key');
            if (!key) {
                googleMapsPromise = Promise.reject(new Error('Google Maps API key is missing.'));
                return googleMapsPromise;
            }

            // Inject our own bootstrap requesting libraries=places so it's a SUPERSET — if this
            // wins the race against the autocomplete loader, google.maps.places stays available
            // for the typeahead to reuse. Resolve via polling (see waitForGoogleMaps above).
            var script = document.createElement('script');
            script.src = 'https://maps.googleapis.com/maps/api/js?key=' + encodeURIComponent(key) +
                '&libraries=places&v=weekly';
            script.async = true;
            script.defer = true;
            document.head.appendChild(script);
        }

        googleMapsPromise = waitForGoogleMaps();
        return googleMapsPromise;
    }

    function parseCoordinate(value) {
        if (value === null || value === undefined || value === '') return null;
        var number = parseFloat(value);
        return Number.isFinite(number) ? number : null;
    }

    function getMessageEl(container) {
        var scope = container.closest('.card__body, .modal__body') || container.parentNode;
        return scope ? scope.querySelector('[data-acqs-property-map-message]') : null;
    }

    function setMessage(container, message) {
        var el = getMessageEl(container);
        if (!el) return;
        if (message) {
            el.textContent = message;
            el.hidden = false;
        } else {
            el.hidden = true;
        }
    }

    function getCompsMessageEl(container) {
        var scope = container.closest('.modal__body') || container.parentNode;
        return scope ? scope.querySelector('[data-acqs-comps-map-message]') : null;
    }

    function setCompsMessage(container, message) {
        var el = getCompsMessageEl(container);
        if (!el) return;
        if (message) {
            el.textContent = message;
            el.hidden = false;
        } else {
            el.hidden = true;
        }
    }

    function isVisible(el) {
        return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
    }

    function addMarker(map, position, title) {
        return new google.maps.Marker({
            map: map,
            position: position,
            title: title || '',
        });
    }

    function formatMoney(value) {
        if (value === null || value === undefined || value === '') return '';
        return '$' + Number(value).toLocaleString('en-US', { maximumFractionDigits: 0 });
    }

    function formatNumber(value) {
        if (value === null || value === undefined || value === '') return '';
        return Number(value).toLocaleString('en-US', { maximumFractionDigits: 0 });
    }

    function formatDate(value) {
        if (!value) return '';
        var date = new Date(value + 'T12:00:00');
        if (Number.isNaN(date.getTime())) return value;
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }

    function escapeHtml(value) {
        var div = document.createElement('div');
        div.textContent = value || '';
        return div.innerHTML;
    }

    function compInfoHeader(comp) {
        return {
            title: comp.address || comp.full_address || 'Comparable sale',
            location: comp.location || '',
        };
    }

    function subjectInfoHeader(subject) {
        return {
            title: subject.address || 'Subject property',
            location: subject.location || '',
        };
    }

    function buildHeaderNode(headerInfo) {
        var wrap = document.createElement('div');
        wrap.className = 'acqs-map-info-header';

        var title = document.createElement('div');
        title.className = 'acqs-map-info-header__title';
        title.textContent = headerInfo.title || '';
        wrap.appendChild(title);

        if (headerInfo.location) {
            var subtitle = document.createElement('div');
            subtitle.className = 'acqs-map-info-header__subtitle';
            subtitle.textContent = headerInfo.location;
            wrap.appendChild(subtitle);
        }
        return wrap;
    }

    function headerAriaLabel(headerInfo) {
        var parts = [];
        if (headerInfo.title) parts.push(headerInfo.title);
        if (headerInfo.location) parts.push(headerInfo.location);
        return parts.join(', ');
    }

    function openMapInfo(infoWindow, map, marker, headerInfo, content) {
        var hasHeaderContent = typeof infoWindow.setHeaderContent === 'function';
        if (hasHeaderContent) {
            infoWindow.setHeaderContent(buildHeaderNode(headerInfo));
            infoWindow.setOptions({ ariaLabel: headerAriaLabel(headerInfo) });
        }
        infoWindow.setContent(content);
        infoWindow.open(map, marker);
    }

    function subjectInfoHtml(subject, includeTitle) {
        var facts = [];
        if (subject.bedrooms) facts.push(subject.bedrooms + 'bd');
        if (subject.bathrooms) facts.push(Number(subject.bathrooms).toLocaleString('en-US', { maximumFractionDigits: 1 }) + 'ba');
        if (subject.sq_feet) facts.push(formatNumber(subject.sq_feet) + ' sqft');

        return '<div class="acqs-map-info" data-theme="light">' +
            (includeTitle ? '<h6 class="text-strong fw-semibold">' + escapeHtml(subject.address || 'Subject property') + '</h6>' : '') +
            (includeTitle && subject.location ? '<p class="text-secondary">' + escapeHtml(subject.location) + '</p>' : '') +
            (subject.label === '' ? '' : '<p class="text-strong fw-semibold">' + escapeHtml(subject.label || 'Subject property') + '</p>') +
            (facts.length ? '<p class="text-muted">' + escapeHtml(facts.join(' · ')) + '</p>' : '') +
            '<p><a target="_blank" rel="noopener" href="' + escapeHtml(subject.google_maps_link) + '">Google Maps</a>' +
            (subject.zillow_link ? ' · <a target="_blank" rel="noopener" href="' + escapeHtml(subject.zillow_link) + '">Zillow</a>' : '') +
            '</p>' +
        '</div>';
    }

    function compInfoHtml(comp, includeTitle) {
        var facts = [];
        if (comp.bedrooms) facts.push(comp.bedrooms + 'bd');
        if (comp.bathrooms) facts.push(Number(comp.bathrooms).toLocaleString('en-US', { maximumFractionDigits: 1 }) + 'ba');
        if (comp.sq_feet) facts.push(formatNumber(comp.sq_feet) + ' sqft');
        if (comp.distance_miles !== null && comp.distance_miles !== undefined) {
            facts.push(Number(comp.distance_miles).toFixed(1) + ' mi');
        }

        var saleDate = comp.sale_date ? 'Sold ' + formatDate(comp.sale_date) : '';
        var salePriceParts = [];
        if (comp.sale_price) salePriceParts.push(formatMoney(comp.sale_price));
        if (comp.price_per_sqft) salePriceParts.push(formatMoney(comp.price_per_sqft) + '/sf');
        var salePrice = salePriceParts.join(' · ');

        var headerInfo = compInfoHeader(comp);
        return '<div class="acqs-map-info" data-theme="light">' +
            (includeTitle ? '<h6 class="text-strong fw-semibold">' + escapeHtml(headerInfo.title) + '</h6>' : '') +
            (includeTitle && headerInfo.location ? '<p class="text-secondary">' + escapeHtml(headerInfo.location) + '</p>' : '') +
            (saleDate ? '<p class="text-muted">' + escapeHtml(saleDate) + '</p>' : '') +
            (salePrice ? '<p class="text-strong fw-semibold">' + escapeHtml(salePrice) + '</p>' : '') +
            (facts.length ? '<p class="text-muted">' + escapeHtml(facts.join(' · ')) + '</p>' : '') +
            '<p><a target="_blank" rel="noopener" href="' + escapeHtml(comp.google_maps_link) + '">Google Maps</a>' +
            (comp.zillow_link ? ' · <a target="_blank" rel="noopener" href="' + escapeHtml(comp.zillow_link) + '">Zillow</a>' : '') +
            '</p>' +
        '</div>';
    }

    function renderMap(container, position) {
        var map = new google.maps.Map(container, {
            center: position,
            zoom: DEFAULT_ZOOM,
            mapTypeControl: true,
            streetViewControl: true,
            fullscreenControl: true,
        });
        var marker = addMarker(map, position, container.getAttribute('data-address'));
        // Clicking the pin opens the same popup as the comps map's subject pin,
        // without its "Subject property" line.
        var subject = {
            address: container.getAttribute('data-title') || container.getAttribute('data-address'),
            location: container.getAttribute('data-location'),
            bedrooms: container.getAttribute('data-bedrooms'),
            bathrooms: container.getAttribute('data-bathrooms'),
            sq_feet: container.getAttribute('data-sq-feet'),
            zillow_link: container.getAttribute('data-zillow-link'),
            google_maps_link: container.getAttribute('data-google-maps-link'),
            label: '',
        };
        var infoWindow = new google.maps.InfoWindow();
        marker.addListener('click', function () {
            var hasHeaderContent = typeof infoWindow.setHeaderContent === 'function';
            openMapInfo(infoWindow, map, marker, subjectInfoHeader(subject), subjectInfoHtml(subject, !hasHeaderContent));
        });
        container._acqsPropertyMap = map;
        container._acqsPropertyMapCenter = position;
        container.setAttribute('data-acqs-property-map-initialized', 'true');
        setMessage(container, '');
    }

    function addCompsSubjectMarker(map, position, subject, infoWindow) {
        var safeSubject = subject || {};
        var headerInfo = subjectInfoHeader(safeSubject);
        var marker = new google.maps.Marker({
            map: map,
            position: position,
            title: headerInfo.title,
            label: 'S',
            zIndex: 100,
        });
        marker.addListener('click', function () {
            var hasHeaderContent = typeof infoWindow.setHeaderContent === 'function';
            openMapInfo(infoWindow, map, marker, headerInfo, subjectInfoHtml(safeSubject, !hasHeaderContent));
        });
        return marker;
    }

    function addCompsMarker(map, comp, infoWindow) {
        var headerInfo = compInfoHeader(comp);
        var marker = new google.maps.Marker({
            map: map,
            position: { lat: comp.lat, lng: comp.lng },
            title: headerInfo.title || comp.location || '',
            label: String(comp.index),
        });
        marker.addListener('click', function () {
            var hasHeaderContent = typeof infoWindow.setHeaderContent === 'function';
            openMapInfo(infoWindow, map, marker, headerInfo, compInfoHtml(comp, !hasHeaderContent));
        });
        return marker;
    }

    function readCompsPayload(container) {
        var jsonId = container.getAttribute('data-map-json-id');
        var el = jsonId ? document.getElementById(jsonId) : null;
        if (!el) return null;
        try {
            return JSON.parse(el.textContent || '{}');
        } catch (e) {
            return null;
        }
    }

    function fitCompsMap(map, bounds, fallbackCenter) {
        if (!bounds.isEmpty()) {
            map.fitBounds(bounds);
            return;
        }
        if (fallbackCenter) {
            map.setCenter(fallbackCenter);
            map.setZoom(COMPS_DEFAULT_ZOOM);
        }
    }

    function renderCompsMap(container, payload, subjectPosition, subjectOnly) {
        var center = subjectPosition;
        if (!center && !subjectOnly && payload.comps && payload.comps.length) {
            center = { lat: payload.comps[0].lat, lng: payload.comps[0].lng };
        }
        if (!center) {
            setCompsMessage(
                container, subjectOnly ? SUBJECT_EMPTY_MESSAGE : COMPS_EMPTY_MESSAGE
            );
            return;
        }

        var map = new google.maps.Map(container, {
            center: center,
            zoom: COMPS_DEFAULT_ZOOM,
            mapTypeControl: true,
            streetViewControl: true,
            fullscreenControl: true,
        });
        var bounds = new google.maps.LatLngBounds();
        var infoWindow = new google.maps.InfoWindow();

        if (subjectPosition) {
            addCompsSubjectMarker(map, subjectPosition, payload.subject || {}, infoWindow);
            bounds.extend(subjectPosition);
        }
        if (!subjectOnly) {
            (payload.comps || []).forEach(function (comp) {
                if (typeof comp.lat !== 'number' || typeof comp.lng !== 'number') return;
                addCompsMarker(map, comp, infoWindow);
                bounds.extend({ lat: comp.lat, lng: comp.lng });
            });
        }

        if (subjectOnly) {
            map.setCenter(center);
            map.setZoom(DEFAULT_ZOOM);
        } else {
            fitCompsMap(map, bounds, center);
        }
        container._acqsCompsMap = map;
        container._acqsCompsMapBounds = bounds;
        container._acqsCompsMapCenter = center;
        container._acqsCompsMapSubjectOnly = subjectOnly;
        container.setAttribute('data-acqs-comps-map-initialized', 'true');
        setCompsMessage(container, '');
    }

    function initCompsMap(container) {
        if (!container) return;
        if (container.hasAttribute('data-acqs-comps-map-initialized')) {
            if (isVisible(container) && container._acqsCompsMap) {
                google.maps.event.trigger(container._acqsCompsMap, 'resize');
                if (container._acqsCompsMapSubjectOnly) {
                    container._acqsCompsMap.setCenter(container._acqsCompsMapCenter);
                    container._acqsCompsMap.setZoom(DEFAULT_ZOOM);
                } else {
                    fitCompsMap(container._acqsCompsMap, container._acqsCompsMapBounds, null);
                }
            }
            return;
        }
        if (!isVisible(container)) return;

        var payload = readCompsPayload(container);
        var subjectOnly = container.hasAttribute('data-acqs-map-subject-only');
        if (!payload || (!subjectOnly && (!payload.comps || !payload.comps.length))) {
            setCompsMessage(
                container, subjectOnly ? SUBJECT_EMPTY_MESSAGE : COMPS_EMPTY_MESSAGE
            );
            return;
        }
        setCompsMessage(container, 'Loading map...');

        loadGoogleMaps(container).then(function () {
            var subject = payload.subject || {};
            if (typeof subject.lat === 'number' && typeof subject.lng === 'number') {
                renderCompsMap(
                    container, payload, { lat: subject.lat, lng: subject.lng }, subjectOnly
                );
                return;
            }
            if (subject.address && google.maps.Geocoder) {
                var geocoder = new google.maps.Geocoder();
                geocoder.geocode({ address: subject.address }, function (results, status) {
                    var position = null;
                    if (status === 'OK' && results && results[0]) {
                        position = results[0].geometry.location;
                    }
                    renderCompsMap(container, payload, position, subjectOnly);
                });
                return;
            }
            renderCompsMap(container, payload, null, subjectOnly);
        }).catch(function () {
            setCompsMessage(container, 'Map unavailable. Use the Google Maps links instead.');
        });
    }

    function recenter(container) {
        var map = container._acqsPropertyMap;
        var center = container._acqsPropertyMapCenter;
        if (!map || !center) return;
        google.maps.event.trigger(map, 'resize');
        map.setCenter(center);
    }

    function geocodeAddress(container, address) {
        if (!google.maps.Geocoder) {
            setMessage(container, 'Open this address in Google Maps to view the location.');
            return;
        }
        setMessage(container, 'Locating address...');
        var geocoder = new google.maps.Geocoder();
        geocoder.geocode({ address: address }, function (results, status) {
            if (status !== 'OK' || !results || !results[0]) {
                setMessage(container, 'Map unavailable for this address. Use Open in Google Maps instead.');
                return;
            }
            renderMap(container, results[0].geometry.location);
        });
    }

    function initMap(container) {
        if (!container) return;
        if (container.hasAttribute('data-acqs-property-map-initialized')) {
            if (isVisible(container)) recenter(container);
            return;
        }
        if (!isVisible(container)) return;
        setMessage(container, 'Loading map...');

        loadGoogleMaps(container).then(function () {
            var lat = parseCoordinate(container.getAttribute('data-lat'));
            var lng = parseCoordinate(container.getAttribute('data-lng'));
            if (lat !== null && lng !== null) {
                renderMap(container, { lat: lat, lng: lng });
                return;
            }

            var address = container.getAttribute('data-address');
            if (address) {
                geocodeAddress(container, address);
                return;
            }

            setMessage(container, 'No location data. Add an address first.');
        }).catch(function () {
            setMessage(container, 'Map unavailable. Use Open in Google Maps instead.');
        });
    }

    function initVisibleMaps(root) {
        root = root || document;
        if (root.matches && root.matches('[data-acqs-property-map]')) {
            initMap(root);
        }
        root.querySelectorAll('[data-acqs-property-map]').forEach(function (container) {
            initMap(container);
        });
        if (root.matches && root.matches('[data-acqs-comps-map]')) {
            initCompsMap(root);
        }
        root.querySelectorAll('[data-acqs-comps-map]').forEach(function (container) {
            initCompsMap(container);
        });
    }

    function initDocument() {
        initVisibleMaps(document);
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initDocument);
    } else {
        initDocument();
    }

    document.addEventListener('tab-changed', function (e) {
        if (!e.detail || e.detail.tab !== 'map') return;
        initVisibleMaps(e.target.parentNode || document);
    });

    document.body.addEventListener('htmx:afterSettle', function (e) {
        initVisibleMaps(e.target || document);
    });

    document.body.addEventListener('sheet:ready', function (e) {
        initVisibleMaps(e.target || document);
    });

    document.body.addEventListener('activateAcqsTab', function (e) {
        var name = typeof e.detail === 'string' ? e.detail : (e.detail && e.detail.value);
        if (name !== 'map') return;
        window.requestAnimationFrame(function () {
            initVisibleMaps(document);
        });
    });

    // The record pages' rail Map section (acqs/_property_map_card.html) is
    // hidden until its rail button opens it.
    document.addEventListener('shell:panel-open', function (e) {
        initVisibleMaps(e.target);
    });

    // The map overlay is fetched on open (`data-overlay-url` →
    // `acqs:property-map-modal`) and lands in <body> already open, so this is
    // where its map gets built. Nothing initialises it before this point,
    // which is the whole reason the overlay moved to its own view: rendered
    // into the page shut it was `visibility: hidden` — still laid out, so the
    // `isVisible` guard passed and a billable Google Maps load fired on every
    // deal and lead page view.
    document.body.addEventListener('seeme:overlay-loaded', function (e) {
        initVisibleMaps(e.target || document);
    });

    window.AcqsPropertyMap = {
        init: initVisibleMaps,
    };
})();
